import { Box, Grid } from '@material-ui/core';
import IconWithTooltip from '../../Tooltip/IconWithTooltip';
import classNames from 'classnames';
import CustomKeyboardDateTimePicker from '../../KeyboardDateTimePicker/KeyboardDateTimePicker';
import SelectField from '../../SelectField/SelectField';
import React, { useMemo } from 'react';
import { CreateNewPortalUserFormData, useStyles } from '../utils';
import { useTranslation } from 'react-i18next';
import { FormikErrors } from 'formik';
import { convertCurrentDateToUserTimeZone } from '../../../views/PortalUsers/utils';
import i18n from '../../../services/i18n';
import { LocaleLanguages } from '../../../store/types/TimeZone';
import { useStatusSectionData } from '../../../hooks/users/useStatusSectiondata';

interface StatusSectionProps {
    values: CreateNewPortalUserFormData;
    initialValues: CreateNewPortalUserFormData;
    handleChange: (e: React.ChangeEvent<any>) => void;
    errors: FormikErrors<CreateNewPortalUserFormData>;
    setFieldValue: (
        field: string,
        value: any,
        shouldValidate?: boolean,
    ) => void;
    setFieldError: (field: string, message: string | undefined) => void;
}

const StatusSection: React.VFC<StatusSectionProps> = ({
    values,
    setFieldValue,
    setFieldError,
    errors,
    initialValues,
}) => {
    const classes = useStyles();
    const { t } = useTranslation();

    const {
        timeZonesList,
        iTimeZone,
        userDateFormat,
        minDateForActivation,
        languagesList,
    } = useStatusSectionData();

    const minForExpiration = useMemo(() => {
        const tzName = timeZonesList.find((e) => e.i_time_zone === iTimeZone)
            ?.time_zone_name;
        const tomorrow = convertCurrentDateToUserTimeZone(tzName);
        tomorrow.setDate(tomorrow.getDate() + 1);
        if (!values.activationDate) {
            return tomorrow;
        }
        const minDay = new Date(values.activationDate.toString()) || tomorrow;
        minDay.setDate(minDay.getDate() + 1);
        return minDay;
    }, [values.activationDate, timeZonesList, iTimeZone]);

    const filteredLanguages = useMemo(() => {
        const langCodesAllowed = i18n?.options?.resources
            ? Object.keys(i18n?.options?.resources)
            : ['en'];
        const langs =
            languagesList?.filter(
                (e) =>
                    langCodesAllowed?.findIndex(
                        (c) =>
                            c.toLocaleLowerCase().replace(/-_\s /gi, '') ===
                            e.iso_639_1
                                .toLocaleLowerCase()
                                .replace(/-_\s /gi, ''),
                    ) !== -1,
            ) ?? [];
        const selectLanguage = values?.language;
        if (
            selectLanguage &&
            langs.findIndex((e) => e.name === selectLanguage) === -1
        ) {
            const name = selectLanguage.includes(
                t('screens:myProfile.languageNotSupported'),
            )
                ? selectLanguage
                : selectLanguage + t('screens:myProfile.languageNotSupported');
            const obj: LocaleLanguages = {
                name: name,
                direction: '',
                iso_639_1: '',
            };
            langs.push(obj);
            initialValues.language = name;
            setFieldValue('language', name);
        }
        return langs;
    }, [values, i18n, t, languagesList, initialValues]);

    return (
        <Grid item className={classes.itemsContainer}>
            <Box className={classes.headerBox} width={350}>
                <span className={classes.rowBoxHeader}>
                    {t('screens:portalUsers.changeStatus')}
                </span>

                <IconWithTooltip
                    dataQa="my-company-address"
                    tooltipText={t('tooltips:myCompany.changeStatus')}
                />
            </Box>
            <Box className={classNames(classes.rowBox, classes.rowBoxSecond)}>
                <CustomKeyboardDateTimePicker
                    id="activationDate"
                    label={t('screens:portalUsers.activationDate')}
                    value={values.activationDate}
                    onChange={(v) => setFieldValue('activationDate', v)}
                    dataQa={'portal-user-activation-date'}
                    dataTestId={'portal-user-activation-date'}
                    minDate={minDateForActivation}
                    userDateTimeFormat={userDateFormat}
                    allowEmptyDate={true}
                    withHours={false}
                    setFieldError={setFieldError}
                    helperText={errors?.activationDate}
                />

                {/* Temporary fix */}
                <div style={{ height: 56 }} />

                <CustomKeyboardDateTimePicker
                    id="expirationDate"
                    label={t('screens:portalUsers.expirationDate')}
                    value={values.expirationDate}
                    onChange={(v) => setFieldValue('expirationDate', v)}
                    dataQa={'portal-user-expieration-date'}
                    dataTestId={'portal-user-activexpierationation-date'}
                    userDateTimeFormat={userDateFormat}
                    allowEmptyDate={true}
                    withHours={false}
                    minDate={minForExpiration}
                    setFieldError={setFieldError}
                    helperText={errors?.expirationDate}
                />
            </Box>
            <Box
                className={classNames(
                    classes.rowBox,
                    classes.timeZoneRow,
                    classes.rowBoxSecond,
                )}
            >
                <SelectField
                    id="timezone"
                    onChange={(e, v) => setFieldValue('timezone', v)}
                    label={t('common:timezone')}
                    items={timeZonesList?.map((v) => v.time_zone_name) || []}
                    value={values.timezone}
                    dataQa="create-portal-user-timezone"
                />

                <SelectField
                    id="language"
                    onChange={(e, v) => setFieldValue('language', v)}
                    label={t('common:language')}
                    items={filteredLanguages?.map((v) => v.name) || []}
                    value={values.language}
                    dataQa="create-portal-user-language"
                />
            </Box>
        </Grid>
    );
};

export default StatusSection;
